import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxChartComponent,
  DxChartModule,
  DxLoadPanelModule,
  DxPieChartComponent,
  DxPieChartModule,
  DxToolbarModule,
} from 'devextreme-angular';
import { DxoArgumentAxisModule, DxoConnectorModule, DxoLabelModule, DxoSeriesTemplateModule } from 'devextreme-angular/ui/nested';
import { SeriesType } from 'devextreme/common/charts';
import { PieChartType } from 'devextreme/viz/pie_chart';
import { ChartSettingDTO, DlistDto } from '../../models/ortak.dtos';

const CIRCULAR_CHART_TYPES = ['pie', 'doughnut', 'donut'];
const DEFAULT_CHART_TYPE: SeriesType = 'bar';

// GEN_SORGU_AYAR (AyarGrup='CHART') üzerinden yönetim ekranından açılıp
// kapatılan pie/donut "Top-N + Diğer" özelliği. Ayar hiç yoksa (backend/admin
// henüz set etmemişse) davranış aynen eskisi gibi kalır - tüm dilimler gösterilir.
const TOP_N_ENABLED_KEY = 'TOP_N_ENABLED';
const TOP_N_COUNT_KEY = 'TOP_N_COUNT';
const TOP_N_OTHER_LABEL_KEY = 'TOP_N_OTHER_LABEL';
const DEFAULT_TOP_N_COUNT = 10;
const DEFAULT_OTHER_LABEL = 'Diğer';

// Kartezyen (dx-chart) grafiklerde kolon başına seri tipi/ekseni override'ı.
// KolonAdi dolu CHART ayarları: SERIES_TYPE (bar/stackedbar/fullstackedbar/line/
// spline/area/... - devextreme SeriesType), SERIES_AXIS ('secondary' verilirse
// o seri sağdaki ikinci value-axis'e bağlanır). İkisi de yoksa DList'in tek
// chartType'ı ve tek (varsayılan) eksen kullanılır - eski davranış değişmedi.
const SERIES_TYPE_KEY = 'SERIES_TYPE';
const SERIES_AXIS_KEY = 'SERIES_AXIS';
const SECONDARY_AXIS_NAME = 'secondary';

interface ValueSeriesInfo {
  field: string;
  type: SeriesType;
  axis?: string;
}

// GenSorgu chart yeteneği: PivotArea/PivotSummary'nin GenDlistColumn'daki karşılığı
// olan ChartArea/ChartSummary metadata'sını okuyup, kendisine verilen düz `rows`
// üzerinde dx-chart/dx-pie-chart render eden bağımsız komponent.
// gen-pivot-grid'in bindChart()/PivotGridDataSource/createStore mekanizmasını kullanmaz.
@Component({
  selector: 'dk-gen-chart',
  standalone: true,
  imports: [
    CommonModule,
    DxButtonModule,
    DxChartModule,
    DxPieChartModule,
    DxToolbarModule,
    DxLoadPanelModule,
    DxoSeriesTemplateModule,
    DxoArgumentAxisModule,
    DxoLabelModule,
    DxoConnectorModule,
  ],
  templateUrl: './gen-chart.component.html',
  styleUrls: ['./gen-chart.component.scss'],
})
export class GenChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(DxChartComponent, { static: false }) chart!: DxChartComponent;
  @ViewChild(DxPieChartComponent, { static: false }) pieChart!: DxPieChartComponent;

  private resizeObserver?: ResizeObserver;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  /**
   * DevExtreme chart/pie-chart, konteynerinin gerçek boyutunu SADECE ilk
   * mount anında ölçüyor; sonradan konteyner büyüyüp küçülse bile (örn.
   * panel tam ekrandan çıkınca, ya da lejant/pasta ilk render'da henüz
   * kesinleşmemiş bir boyuta sığdırılmaya çalışılınca) kendiliğinden yeniden
   * ölçmüyor - lejant "sığmıyor" diye sessizce çiziliyor. ResizeObserver ile
   * konteyner boyutu her değiştiğinde widget'ın kendi render()'ı zorlanıyor.
   */
  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.renderChart());
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private renderChart(): void {
    if (this.isCircular) {
      this.pieChart?.instance?.render();
    } else {
      this.chart?.instance?.render();
    }
  }

  @Input() gridCtlList: DlistDto;
  @Input() rows: any[] = [];
  @Input() isLoading = false;
  @Input() legendVisible = true;
  /** Bazı tüketiciler (GenSorguPanelComponent) bu butonu kendi panel header'ına taşıyor - iç toolbar'da tekrarlanmasın diye kapatılabiliyor. */
  @Input() showToolbarLegendButton = true;
  /** Bazı tüketiciler zaten dışarıda kendi chart/liste geçiş butonuna sahip (GenSorguPanelComponent) - iç toolbar'daki "Grid" tekrarını kapatabiliyor. */
  @Input() showGridButton = true;
  /**
   * Pasta/donut lejantının kolon sayısı. Mobilde konteyner genişliği (telefon
   * ekranı, tam ekran açılsa bile ~350-400px) masaüstünün maksimize panelinden
   * çok daha dar - 3 kolonluk lejant satır başına yeterli genişlik bulamayınca
   * DevExtreme lejantı hiç çizmiyor (sessizce gizliyor, hata vermiyor). Mobil
   * tüketici (GenSorguPanelComponent) bunu 1'e düşürerek her etikete tam
   * genişlik veriyor.
   */
  @Input() legendColumnCount = 3;
  @Output() chartModeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() legendVisibleChange = new EventEmitter<boolean>();

  // DevExtreme Angular wrapper Input'ları (argumentField/valueField/nameField) plain
  // `string` bekliyor (nullable değil) - bu yüzden null yerine boş string kullanılıyor.
  argumentField = '';
  valueFields: string[] = [];
  valueSeriesInfo: ValueSeriesInfo[] = [];
  seriesField = '';
  isCircular = false;

  get hasSecondaryAxis(): boolean {
    return this.valueSeriesInfo.some((v) => v.axis === SECONDARY_AXIS_NAME);
  }

  toggleLegend(): void {
    this.legendVisible = !this.legendVisible;
    this.legendVisibleChange.emit(this.legendVisible);
    this.applyLegendVisible();
  }

  /** Pie/donut için (ayar açıksa) 'Diğer' ile toplanmış veri; kapalıysa/tanımsızsa numericRows'un aynısı. */
  circularRows: any[] = [];

  /**
   * Backend rapor satırlarını Dictionary<string,string> olarak döndürüyor (bkz.
   * DABReportServices.FormatOracleValue), yani value alanları JSON'da string
   * geliyor. dx-chart value eksenini alandaki JS tipine bakarak otomatik
   * continuous/discrete seçiyor - string kalınca eksen "kategorik" davranıp
   * etiketleri büyüklüğe göre değil veri sırasına göre diziyor. Bu yüzden
   * chart'a verilmeden önce value alanları number'a çevriliyor (grid hâlâ ham
   * `rows`'u kullanıyor, orada string kalması sorun değil).
   *
   * Hangi value alanının sayısal olduğu tahmin edilmiyor: GenSorguKolon.DataType
   * (MapOracleDataType ile 'string'/'number'/'date'/'lookup' - bkz.
   * DABReportServices.cs) columnsInfo üzerinden field.dataType olarak buraya
   * kadar geliyor, tek doğru kaynak bu.
   */
  numericRows: any[] = [];
  private numericValueFields: string[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['gridCtlList']) {
      this.buildFieldRoles();
    }
    if (changes['gridCtlList'] || changes['rows']) {
      this.buildNumericRows();
      this.buildCircularRows();
    }
    // dx-pie-chart'ta dxo-legend[visible] deklaratif binding'i, GenSorguPanelComponent
    // gibi dışarıdan [legendVisible] Input'u üzerinden geldiğinde bazen yayılmıyor
    // (dx-chart'ta sorun yok) - widget'ın kendi option API'sini zorlayarak garantiye
    // alınıyor.
    if (changes['legendVisible'] && !changes['legendVisible'].firstChange) {
      this.applyLegendVisible();
    }
  }

  private applyLegendVisible(): void {
    if (this.isCircular) {
      this.pieChart?.instance?.option('legend.visible', this.legendVisible);
    } else {
      this.chart?.instance?.option('legend.visible', this.legendVisible);
    }
  }

  private buildNumericRows() {
    const rows = this.rows || [];
    if (!this.numericValueFields.length) {
      this.numericRows = rows;
      return;
    }
    this.numericRows = rows.map((row) => {
      const copy = { ...row };
      this.numericValueFields.forEach((field) => {
        const raw = copy[field];
        if (raw !== null && raw !== undefined && raw !== '') {
          copy[field] = Number(raw);
        }
      });
      return copy;
    });
  }

  private buildFieldRoles() {
    const fields = this.gridCtlList?.fields || [];
    this.argumentField = fields.find((f) => f.chartArea === 'argument')?.dataField || '';
    const valueFieldDefs = fields.filter((f) => f.chartArea === 'value');
    this.valueFields = valueFieldDefs.map((f) => f.dataField);
    this.numericValueFields = valueFieldDefs
      .filter((f) => f.dataType === 'number')
      .map((f) => f.dataField);
    this.seriesField = fields.find((f) => f.chartArea === 'series')?.dataField || '';
    this.isCircular = CIRCULAR_CHART_TYPES.includes(this.gridCtlList?.chartType);

    this.valueSeriesInfo = valueFieldDefs.map((f) => ({
      field: f.dataField,
      type: (this.getColumnChartSetting(f.dataField, SERIES_TYPE_KEY) as SeriesType) || this.seriesType,
      axis:
        this.getColumnChartSetting(f.dataField, SERIES_AXIS_KEY) === SECONDARY_AXIS_NAME
          ? SECONDARY_AXIS_NAME
          : undefined,
    }));
  }

  /** kolonAdi dolu (o kolona özel) CHART ayarı arar. */
  private getColumnChartSetting(kolonAdi: string, ayarKod: string): string | undefined {
    return this.gridCtlList?.chartSettings?.find(
      (s: ChartSettingDTO) => s.ayarKod === ayarKod && s.kolonAdi === kolonAdi
    )?.ayarDeger;
  }

  /** Sorgu geneli (kolonAdi boş) CHART ayarı arar; GEN_SORGU_AYAR henüz boşsa undefined döner. */
  private getChartSetting(ayarKod: string): string | undefined {
    return this.gridCtlList?.chartSettings?.find(
      (s: ChartSettingDTO) => s.ayarKod === ayarKod && !s.kolonAdi
    )?.ayarDeger;
  }

  private buildCircularRows() {
    const rows = this.numericRows || [];
    const topNEnabled = this.getChartSetting(TOP_N_ENABLED_KEY) === 'E';

    if (!this.isCircular || !topNEnabled || !this.argumentField || !this.firstValueField) {
      this.circularRows = rows;
      return;
    }

    const maxSlices = Number(this.getChartSetting(TOP_N_COUNT_KEY)) || DEFAULT_TOP_N_COUNT;
    if (rows.length <= maxSlices) {
      this.circularRows = rows;
      return;
    }

    const otherLabel = this.getChartSetting(TOP_N_OTHER_LABEL_KEY) || DEFAULT_OTHER_LABEL;
    const argField = this.argumentField;
    const valField = this.firstValueField;
    const sorted = [...rows].sort((a, b) => (Number(b[valField]) || 0) - (Number(a[valField]) || 0));
    const top = sorted.slice(0, maxSlices - 1);
    const rest = sorted.slice(maxSlices - 1);
    const otherTotal = rest.reduce((sum, r) => sum + (Number(r[valField]) || 0), 0);

    this.circularRows = [...top, { [argField]: otherLabel, [valField]: otherTotal }];
  }

  get seriesType(): SeriesType {
    return (this.gridCtlList?.chartType as SeriesType) || DEFAULT_CHART_TYPE;
  }

  get pieType(): PieChartType {
    return this.gridCtlList?.chartType === 'pie' ? 'pie' : 'doughnut';
  }

  get firstValueField(): string {
    return this.valueFields[0] || '';
  }

  /** Pasta/donut dilim etiketleri artık dilimin İÇİNDE değil, connector çizgisiyle
   *  DIŞARIDA gösteriliyor (bkz. şablondaki dxo-label/dxo-connector) - "argüman:
   *  değer" biçiminde, DevExtreme'in kendi biçimlendirdiği metinler kullanılıyor. */
  customizePieLabel = (pointInfo: any) => `${pointInfo.argumentText}: ${pointInfo.valueText}`;

  /** Tooltip varsayılan olarak (özellikle pie/donut'ta) sadece değeri gösteriyordu
   *  - hem dx-chart hem dx-pie-chart için x-argümanını da ekliyor. Çok serili
   *  dx-chart'larda (seriesField/valueSeriesInfo) hangi seriye ait olduğu da
   *  parantez içinde eklenir, tek serili/pie'de seriesName boş gelir. */
  customizeTooltip = (pointInfo: any) => ({
    text: pointInfo.seriesName
      ? `${pointInfo.argumentText} (${pointInfo.seriesName}): ${pointInfo.valueText}`
      : `${pointInfo.argumentText}: ${pointInfo.valueText}`,
  });

  printChart() {
    (this.isCircular ? this.pieChart : this.chart)?.instance?.print();
  }

  exportChart(exportFileType: string) {
    (this.isCircular ? this.pieChart : this.chart)?.instance?.exportTo(
      this.gridCtlList?.dlistName || 'Grafik',
      exportFileType
    );
  }

  closeChart() {
    this.chartModeChange.emit(false);
  }
}
